import { useState, useEffect, useCallback } from 'react';
import { auth, saveApiCredentials, getApiCredentials } from '../firebase';
import { capitalApiClient } from '../api/CapitalApiClient';

export const useCapitalCredentials = () => {
  const [apiKey, setApiKey] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [selectedApiUrl, setSelectedApiUrl] = useState('https://api-capital.backend-capital.com/api/v1');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (user) {
        const credentials = await getApiCredentials(user.uid);
        if (credentials) {
          setApiKey(credentials.apiKey || '');
          setIdentifier(credentials.identifier || '');
          setPassword(credentials.password || '');
          setSelectedApiUrl(credentials.selectedApiUrl || 'https://api-capital.backend-capital.com/api/v1');
        }
      }
    } catch (err) {
      console.error('Error loading API credentials:', err);
      setError('Failed to load API credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  useEffect(() => {
    if (apiKey && identifier && password && selectedApiUrl) {
      capitalApiClient.init(apiKey, identifier, password, selectedApiUrl);
      // Force a new session creation
      capitalApiClient.clearSession();
    }
  }, [apiKey, identifier, password, selectedApiUrl]);

  const saveCredentials = async () => {
    setError(null);
    try {
      const user = auth.currentUser;
      if (user) {
        await saveApiCredentials(user.uid, { apiKey, identifier, password, selectedApiUrl });
        // Force a new session creation after saving
        capitalApiClient.init(apiKey, identifier, password, selectedApiUrl);
        capitalApiClient.clearSession();
        return true;
      } else {
        throw new Error('User not authenticated');
      }
    } catch (err) {
      console.error('Error saving API credentials:', err);
      setError('Failed to save API credentials');
      return false;
    }
  };

  const setSelectedApiUrlAndReinitialize = (newUrl) => {
    setSelectedApiUrl(newUrl);
    if (apiKey && identifier && password) {
      capitalApiClient.init(apiKey, identifier, password, newUrl);
      capitalApiClient.clearSession();
    }
  };

  return {
    apiKey,
    setApiKey,
    identifier,
    setIdentifier,
    password,
    setPassword,
    selectedApiUrl,
    setSelectedApiUrl: setSelectedApiUrlAndReinitialize,
    loading,
    error,
    saveCredentials,
    loadCredentials,
  };
};